<template>
  <tr class="hover:bg-gray-100 focus-within:bg-gray-100">
    <template v-if="editableMode">
      <td class="border-t">        
        <vSelect
          v-model="detail.item" 
          class="px-4 py-4 focus:text-indigo-500" 
          :class="{ error: errors.item_id }"
          placeholder="[Pick Item...]" 
          label="name"
          :filterable="false" 
          :options="options"           
          :append-to-body="true"           
          @search="onSearch" 
          @option:selected="setSelectedItem"
        />
        <div v-if="errors.item_id" class="form-error px-4 -py-4 whitespace-normal">{{ errors.item_id }}</div>
      </td>
      <td class="border-t">        
        <vSelect
          v-model="detail.location" 
          label="name" 
          placeholder="[Pick Location...]" 
          class="px-4 py-4"     
          :class="{ error: errors.item_location_id }"          
          :options="locations"           
          :append-to-body="true"
        /> 
        <div v-if="errors.item_location_id" class="form-error px-4 -py-4 whitespace-normal">{{ errors.item_location_id }}</div>
      </td> 
      <td class="border-t text-right">
        <number-input 
          v-model="detail.item_qty" 
          class="px-4 py-4" 
          :error="errors.item_qty" 
        />
      </td>
      <td class="border-t">        
        <vSelect
          v-model="detail.item_unit" 
          label="name" 
          placeholder="[Pick Unit...]" 
          class="px-4 py-4"     
          :class="{ error: errors.item_unit }"          
          :options="unitOptions"           
          :append-to-body="true"
        /> 
        <div v-if="errors.item_unit_id" class="form-error px-4 -py-4 whitespace-normal">{{ errors.item_unit_id }}</div>
      </td> 
      <td class="border-t">
        <text-input v-model="detail.description" :error="errors.description" class="w-full" />
      </td>
      <td class="w-px border-t">
        <div class="flex justify-items-end px-6 py-4">
          <button title="Save Changes" @click="store">
            <Icon icon="ic:baseline-save" class="block w-6 h-6 fill-gray-400" />
          </button>
          <button title="Cancel Edit" @click="cancel">
            <Icon icon="ic:baseline-restart-alt" class="block w-6 h-6 fill-gray-400" />
          </button>
        </div>
      </td>
    </template>
    <template v-else>
      <td class="border-t">
        <div class="flex items-center px-6 focus:text-indigo-500">
          {{ logisticDetail.item.name }}
        </div>
      </td>
      <td class="border-t">
        <div class="flex items-center px-6 py-4">
          {{ logisticDetail.location.name }}
        </div>
      </td>
      <td class="border-t text-right">
        <div class="px-6 py-4 text-right">
          {{ logisticDetail.item_qty }}
        </div>
      </td>
      <td class="border-t">
        <div class="px-6 py-4">
          {{ logisticDetail.item_unit.name }}
        </div>
      </td>
      <td class="border-t">
        <div class="px-6 py-4">
          {{ logisticDetail.description }}
        </div>
      </td>
      <td class="w-px border-t">
        <div class="flex items-center px-6 py-4">
          <button title="Edit Detail" @click="edit">
            <Icon icon="ic:baseline-edit-note" class="block w-6 h-6 fill-gray-400" />
          </button>
          <button title="Delete Detail" @click="destroy">
            <Icon icon="ic:baseline-delete" class="block w-6 h-6 fill-gray-400" />
          </button>
        </div>
      </td>
    </template>
  </tr>
</template>

<script lang="js">
import { Icon } from '@iconify/vue'
import { ref } from 'vue'
import vSelect from 'vue-select'
import NumberInput from '@/Shared/NumberInput'
import TextInput from '@/Shared/TextInput'
import 'vue-select/dist/vue-select.css'
import throttle from 'lodash/throttle'
import clone from 'lodash/clone'

export default {
  components: {
    Icon,
    vSelect,
    NumberInput,
    TextInput,
  },
  props: {
    logisticDetail: Object,
    locations: Object,
    errors: Object,
    units: Object,
    editableMode: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['destroy:detail', 'cancel', 'save', 'edit:detail'],
  setup(props) {
    const options = ref(props.logisticDetail.item ? [{ id: props.logisticDetail.item.id, name: props.logisticDetail.item.name }] : [])
    const detail = ref(props.logisticDetail)
    const oriData = ref(typeof logisticDetail)    
    const search = throttle((loading, search) => {
      fetch(`/items?options=1&search=${escape(search)}`).then((res) => {
        res.json().then((json) => (options.value = json.items))
        loading(false)
      })
    }, 500)
    const unitOptions = ref(props.logisticDetail.item_unit ? [props.logisticDetail.item_unit] : [])

    return {
      search,
      options,
      detail,
      oriData,
      unitOptions,
    }
  },  
  watch: {
    'detail.item': function (newVal) {
      if (newVal) {
        this.detail.item_unit = null
        this.unitOptions = this.units.filter(i => newVal.units.includes( i.id ))
      }
    },
  },
  methods: {
    setSelectedLocation(location) {
      this.detail.location = location
    },
    setSelectedItem(item) {
      this.detail.item = item
    },
    onSearch(search, loading) {
      if (search.length) {
        loading(true)
        this.search(loading, search)
      }
    },
    edit() {
      this.oriData = clone(this.logisticDetail)      
      this.$emit('edit:detail', this.detail)
    },
    cancel() {     
      this.detail = clone(this.oriData)
      this.$emit('cancel')      
    },
    store() {      
      this.$emit('save', this.detail)
    },
    destroy() {
      if (confirm('Are you sure you want to delete this details?')) {
        this.$emit('destroy:detail', this.logisticDetail.id)
      }
    },
  },
}
</script>

<style scoped>
.v-select {
  --vs-line-height: 1.9;
}
</style>